import { MsdDto } from "src/app/services/generatedApi";
import {
  MsdRiskLevel,
  CAUTION_ZONE_THRESHOLD,
  HAZARD_ZONE_THRESHOLD,
  riskFactorPaths,
} from "./constants";

export type MsdScore = {
  lowBackScore: {
    cautionZoneCount: number;
    hazardZoneCount: number;
  };
  handWristScore: {
    cautionZoneCount: number;
    hazardZoneCount: number;
  };
  neckShoulderScore: {
    cautionZoneCount: number;
    hazardZoneCount: number;
  };
  kneeScore: {
    cautionZoneCount: number;
    hazardZoneCount: number;
  };
  totalCautionZoneCount: number;
  totalHazardZoneCount: number;
  riskLevel: MsdRiskLevel;
};

/**
 * Gets a value from a nested object using a dot-notation path string
 */
function getNestedValue(obj: any, path: string): boolean {
  const parts = path.split(".");
  let current = obj;

  for (const part of parts) {
    if (current === undefined || current === null) {
      return false;
    }
    current = current[part];
  }

  return !!current;
}

export function calculateMsdScore(msdData: MsdDto): MsdScore {
  const score: MsdScore = {
    lowBackScore: { cautionZoneCount: 0, hazardZoneCount: 0 },
    handWristScore: { cautionZoneCount: 0, hazardZoneCount: 0 },
    neckShoulderScore: { cautionZoneCount: 0, hazardZoneCount: 0 },
    kneeScore: { cautionZoneCount: 0, hazardZoneCount: 0 },
    totalCautionZoneCount: 0,
    totalHazardZoneCount: 0,
    riskLevel: MsdRiskLevel.LOW,
  };

  // Check low back risk factors
  riskFactorPaths.lowBack.cautionZone.forEach(({ path }) => {
    if (getNestedValue(msdData.lowBack, path)) {
      score.lowBackScore.cautionZoneCount++;
      score.totalCautionZoneCount++;
    }
  });

  riskFactorPaths.lowBack.hazardZone.forEach(({ path }) => {
    if (getNestedValue(msdData.lowBack, path)) {
      score.lowBackScore.hazardZoneCount++;
      score.totalHazardZoneCount++;
    }
  });

  // Check hand and wrist risk factors
  riskFactorPaths.handWrist.cautionZone.forEach(({ path }) => {
    if (getNestedValue(msdData.handWrist, path)) {
      score.handWristScore.cautionZoneCount++;
      score.totalCautionZoneCount++;
    }
  });

  riskFactorPaths.handWrist.hazardZone.forEach(({ path }) => {
    if (getNestedValue(msdData.handWrist, path)) {
      score.handWristScore.hazardZoneCount++;
      score.totalHazardZoneCount++;
    }
  });

  // Check neck and shoulder risk factors
  riskFactorPaths.neckShoulder.cautionZone.forEach(({ path }) => {
    if (getNestedValue(msdData.neckShoulder, path)) {
      score.neckShoulderScore.cautionZoneCount++;
      score.totalCautionZoneCount++;
    }
  });

  riskFactorPaths.neckShoulder.hazardZone.forEach(({ path }) => {
    if (getNestedValue(msdData.neckShoulder, path)) {
      score.neckShoulderScore.hazardZoneCount++;
      score.totalHazardZoneCount++;
    }
  });

  // Check knee risk factors
  riskFactorPaths.knee.cautionZone.forEach(({ path }) => {
    if (getNestedValue(msdData.knee, path)) {
      score.kneeScore.cautionZoneCount++;
      score.totalCautionZoneCount++;
    }
  });

  riskFactorPaths.knee.hazardZone.forEach(({ path }) => {
    if (getNestedValue(msdData.knee, path)) {
      score.kneeScore.hazardZoneCount++;
      score.totalHazardZoneCount++;
    }
  });

  score.riskLevel = determineRiskLevel(
    score.totalCautionZoneCount,
    score.totalHazardZoneCount,
  );

  return score;
}

function determineRiskLevel(
  cautionZoneCount: number,
  hazardZoneCount: number,
): MsdRiskLevel {
  if (hazardZoneCount >= HAZARD_ZONE_THRESHOLD) {
    return hazardZoneCount >= 3 ? MsdRiskLevel.VERY_HIGH : MsdRiskLevel.HIGH;
  }
  if (cautionZoneCount >= CAUTION_ZONE_THRESHOLD) {
    return cautionZoneCount >= 4 ? MsdRiskLevel.HIGH : MsdRiskLevel.MODERATE;
  }
  return MsdRiskLevel.LOW;
}
